import { formatDate, formatDateTime, formatTaka } from "@brandex/i18n";
import { useBrandex } from "@brandex/dashboard";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Select, Spinner } from "@brandex/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { commerceKeys, useChangeRequests, useIsSuperAdmin, useSubscriptions, type Subscription } from "../data";
import { fieldError, QueryError } from "../views";

type Action = "quote" | "activate" | "pause" | "resume" | "cancel" | "keep";

const STATUS_TONE: Record<string, "neutral" | "success" | "warning" | "accent"> = {
  requested: "warning",
  quoted: "accent",
  active: "success",
  paused: "neutral",
  cancelled: "neutral",
};

/**
 * Allowed actions per status (mirrors the backend state machine). Cancelling
 * a running package takes effect at month end; until then it can be kept.
 */
export function actionsFor(status: string, ending = false): Action[] {
  const end: Action = ending ? "keep" : "cancel";
  switch (status) {
    case "requested":
    case "quoted":
      return ["quote", "activate", "cancel"];
    case "active":
      return ["pause", end];
    case "paused":
      return ["resume", end];
    default:
      return [];
  }
}

/** Client subscriptions and doctors' change requests (F-04). */
export function SubscriptionsPage() {
  const subs = useSubscriptions();
  const changes = useChangeRequests();
  return (
    <div className="grid gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">Subscriptions</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Requests from onboarding and the package page. Custom packages need a quote before activation; a quote is valid for 14 days and activation locks the price for 6 months.
        </p>
      </header>
      {subs.isPending ? <Spinner label="Loading…" /> : null}
      {subs.isError ? <QueryError error={subs.error} /> : null}
      {subs.data !== undefined ? (
        <div className="glass overflow-x-auto rounded-[var(--radius-lg)]">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-4 py-3">Organization</th><th>Kind</th><th>Status</th><th className="text-right">Monthly</th><th>Requested</th><th className="px-4">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {subs.data.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No subscriptions yet.</td></tr>
              ) : (
                subs.data.map((s) => <SubscriptionRow key={s.id} s={s} />)
              )}
            </tbody>
          </table>
          {subs.hasNextPage ? (
            <div className="border-t border-border p-3 text-center">
              <Button variant="ghost" size="sm" loading={subs.isFetchingNextPage} onClick={() => void subs.fetchNextPage()}>Load more</Button>
            </div>
          ) : null}
        </div>
      ) : null}
      <ChangeRequestsCard query={changes} />
    </div>
  );
}

function SubscriptionRow({ s }: { s: Subscription }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const superAdmin = useIsSuperAdmin();
  const [price, setPrice] = useState("");
  const [reason, setReason] = useState("");
  const act = useMutation({
    mutationFn: async (action: Action) => {
      const quote = { action, price_paisa: Math.round(Number(price) * 100), ...(reason.trim() === "" ? {} : { override_reason: reason.trim() }) };
      await api.http.POST("/v1/admin/subscriptions/{subscription_id}/actions", {
        params: { path: { subscription_id: s.id } },
        body: action === "quote" ? quote : { action },
      });
    },
    onSuccess: () => {
      setReason("");
      return qc.invalidateQueries({ queryKey: commerceKeys.subscriptions });
    },
  });
  const actions = actionsFor(s.status, s.ends_at != null);
  const reasonError = fieldError(act.error, "override_reason");
  return (
    <tr className="align-top">
      <td className="px-4 py-3"><code className="text-xs">{s.organization_id.slice(-8)}</code>{s.note ? <p className="mt-1 max-w-xs text-xs text-muted-foreground">{s.note}</p> : null}</td>
      <td className="py-3 capitalize">{s.kind}</td>
      <td className="py-3"><Badge tone={STATUS_TONE[s.status] ?? "neutral"}>{s.status}</Badge><Terms s={s} /></td>
      <td className="py-3 text-right tabular-nums">{s.monthly_price_paisa !== null && s.monthly_price_paisa !== undefined ? formatTaka("en", s.monthly_price_paisa) : "To quote"}</td>
      <td className="py-3 text-muted-foreground">{formatDateTime("en", s.requested_at)}</td>
      <td className="px-4 py-3">
        {act.isError && reasonError === undefined ? <div className="mb-2"><QueryError error={act.error} /></div> : null}
        <div className="flex flex-wrap items-center gap-2">
          {actions.includes("quote") ? (
            <>
              <Input aria-label="Quote price (৳)" className="h-8 w-28" inputMode="numeric" placeholder="৳ price" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ""))} />
              <Button size="sm" variant="outline" disabled={price === ""} loading={act.isPending && act.variables === "quote"} onClick={() => act.mutate("quote")}>Quote</Button>
            </>
          ) : null}
          {actions.filter((a) => a !== "quote").map((a) => (
            <Button key={a} size="sm" variant={a === "cancel" ? "ghost" : a === "activate" ? "primary" : "outline"} loading={act.isPending && act.variables === a} onClick={() => act.mutate(a)}>
              {a[0]?.toUpperCase()}{a.slice(1)}
            </Button>
          ))}
        </div>
        {actions.includes("quote") && superAdmin ? (
          <div className="mt-2 grid gap-1">
            <Input
              aria-label="Reason for a price below the margin floor"
              aria-invalid={reasonError !== undefined}
              className="h-8 w-full max-w-xs"
              maxLength={500}
              placeholder="Reason, only if below the floor"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {reasonError !== undefined ? <p className="text-xs text-destructive">{reasonError}</p> : null}
          </div>
        ) : null}
        {s.status === "active" && s.ends_at == null ? <Prepay s={s} /> : null}
      </td>
    </tr>
  );
}

/** Quote validity, price lock and any approved below-floor price (ADR 0016 §5). */
function Terms({ s }: { s: Subscription }) {
  const expired = s.status === "quoted" && s.quote_expires_at != null && new Date(s.quote_expires_at) < new Date();
  return (
    <ul className="mt-1 grid gap-0.5 text-xs text-muted-foreground">
      {s.status === "quoted" && s.quote_expires_at != null ? (
        <li className={expired ? "font-medium text-destructive" : undefined}>
          {expired ? "Quote expired " : "Quote valid until "}{formatDate("en", s.quote_expires_at)}
        </li>
      ) : null}
      {s.price_locked_until != null ? <li>Price locked until {formatDate("en", s.price_locked_until)}</li> : null}
      {s.below_floor_approved ? <li><Badge tone="warning">Below floor · approved</Badge></li> : null}
      {s.ends_at != null ? <li className="font-medium text-destructive">Ends {formatDate("en", new Date(Date.parse(s.ends_at) - 1000).toISOString())} (cancelled)</li> : null}
    </ul>
  );
}

function ChangeRequestsCard({ query }: { query: ReturnType<typeof useChangeRequests> }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const resolve = useMutation({
    mutationFn: async (id: string) => {
      await api.http.POST("/v1/admin/change-requests/{change_request_id}/resolve", { params: { path: { change_request_id: id } } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: commerceKeys.changeRequests }),
  });
  return (
    <Card>
      <CardHeader><CardTitle>Change requests</CardTitle></CardHeader>
      <CardContent>
        {query.isError ? <QueryError error={query.error} /> : null}
        {query.data?.length === 0 ? <p className="text-sm text-muted-foreground">No open requests.</p> : null}
        <ul className="divide-y divide-border">
          {query.data?.map((c) => (
            <li key={c.id} className="flex items-start justify-between gap-4 py-3">
              <div>
                <p className="text-sm">{c.message}</p>
                <p className="text-xs text-muted-foreground"><code>{c.organization_id.slice(-8)}</code> · {formatDateTime("en", c.created_at)}</p>
              </div>
              <Button size="sm" variant="outline" loading={resolve.isPending && resolve.variables === c.id} onClick={() => resolve.mutate(c.id)}>Resolve</Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/** Next month in Dhaka, `YYYY-MM`: the earliest sensible prepay start. */
function nextPeriod(): string {
  const [y, m] = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Dhaka", year: "numeric", month: "2-digit" }).format(new Date()).split("-").map(Number);
  const year = (y ?? 2026) + ((m ?? 1) === 12 ? 1 : 0);
  const month = ((m ?? 1) % 12) + 1;
  return `${year}-${String(month).padStart(2, "0")}`;
}

/** Issue a 6- or 12-month invoice at the prepayment discount (5% / 10%). */
function Prepay({ s }: { s: Subscription }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [months, setMonths] = useState<6 | 12>(12);
  const [start, setStart] = useState(nextPeriod);
  const prepay = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/admin/subscriptions/{subscription_id}/prepay", {
        params: { path: { subscription_id: s.id } },
        body: { start, months },
      });
    },
    onSuccess: () => {
      setOpen(false);
      return qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
    },
  });
  const price = s.monthly_price_paisa ?? 0;
  const total = Math.round(price * months * (months === 12 ? 0.9 : 0.95));
  if (!open) {
    return (
      <div className="mt-2">
        {prepay.isSuccess ? <p className="mb-1 text-xs text-muted-foreground">Prepaid invoice created (see Payments).</p> : null}
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>Prepay…</Button>
      </div>
    );
  }
  return (
    <form className="mt-2 grid max-w-xs gap-2 rounded-md border border-border p-3" onSubmit={(e) => { e.preventDefault(); prepay.mutate(); }}>
      <Select aria-label="Months to prepay" value={months} onChange={(e) => setMonths(Number(e.target.value) as 6 | 12)}>
        <option value={6}>6 months (5% off)</option>
        <option value={12}>12 months (10% off)</option>
      </Select>
      <Input aria-label="First month" type="month" value={start} onChange={(e) => e.target.value && setStart(e.target.value)} />
      <p className="text-xs text-muted-foreground">Invoice total {formatTaka("en", total)}</p>
      {prepay.isError ? <QueryError error={prepay.error} /> : null}
      <div className="flex gap-2">
        <Button size="sm" type="submit" loading={prepay.isPending}>Create invoice</Button>
        <Button size="sm" variant="ghost" type="button" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </form>
  );
}
